import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import axiosClient from '../../actions/axios-client';

/** Reads `qr-code-text` from Axios plain headers or AxiosHeaders (axios 1.x). */
function getQrCodeTextHeader(headers) {
  if (!headers) return null;
  const raw =
    typeof headers.get === 'function'
      ? headers.get('qr-code-text')
      : headers['qr-code-text'];
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s ? s : null;
}

function QrCodePanel({ title, description, imageUrl, linkHref, downloadName }) {
  if (!imageUrl) {
    return null;
  }

  return (
    <div className="glass-panel p-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="m-0 text-lg font-bold text-[#121212]">{title}</h3>
        <a
          href={imageUrl}
          download={downloadName}
          className="text-[var(--bethere-primary)] transition-colors hover:text-[var(--bethere-tertiary)]"
          title="Download QR Code"
          aria-label={`Download ${title}`}
        >
          <FontAwesomeIcon
            icon={['fas', 'download']}
            className="text-[length:var(--bethere-f-p)]"
          />
        </a>
      </div>
      {description ? (
        <p className="mb-4 text-sm leading-relaxed text-[var(--festy-muted-text)]">
          {description}
        </p>
      ) : null}
      <img src={imageUrl} alt={title} className="bethere-qr mx-auto block" />
      {linkHref ? (
        <p className="mt-4 text-center text-xs">
          <a
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-[var(--bethere-primary)] underline-offset-2 hover:text-[var(--bethere-tertiary)] hover:underline"
          >
            Open festival link
          </a>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Right-rail QR section for the edit festival layout (bethere/Add-Event.html).
 * Shows public and/or preview codes depending on status.
 */
export function FestivalQrCodesSidebar({ festivalId, status }) {
  const [publicUrl, setPublicUrl] = useState(null);
  const [privateUrl, setPrivateUrl] = useState(null);
  const [publicLinkHref, setPublicLinkHref] = useState(null);
  const [privateLinkHref, setPrivateLinkHref] = useState(null);
  const urlsRef = useRef([]);

  useEffect(() => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setPublicUrl(null);
    setPrivateUrl(null);
    setPublicLinkHref(null);
    setPrivateLinkHref(null);

    if (!festivalId || status === 'DELETED') {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      const wantPublic = status === 'PUBLISHED' || status === 'PREVIEW';
      const wantPrivate = status === 'UNPUBLISHED' || status === 'PREVIEW';

      const fetches = [];
      if (wantPublic) {
        fetches.push(
          axiosClient
            .get(`/admin/festival/${festivalId}/qrcode`, {
              responseType: 'blob',
              headers: { Accept: 'image/png' },
            })
            .then((r) => ({
              key: 'public',
              blob: r.data,
              linkHref: getQrCodeTextHeader(r.headers),
            })),
        );
      }
      if (wantPrivate) {
        fetches.push(
          axiosClient
            .get(`/admin/festival/${festivalId}/previewCode`, {
              responseType: 'blob',
              headers: { Accept: 'image/png' },
            })
            .then((r) => ({
              key: 'private',
              blob: r.data,
              linkHref: getQrCodeTextHeader(r.headers),
            })),
        );
      }

      try {
        const results = await Promise.all(fetches);
        if (cancelled) return;
        for (const { key, blob, linkHref } of results) {
          const u = URL.createObjectURL(blob);
          urlsRef.current.push(u);
          if (key === 'public') {
            setPublicUrl(u);
            setPublicLinkHref(linkHref);
          }
          if (key === 'private') {
            setPrivateUrl(u);
            setPrivateLinkHref(linkHref);
          }
        }
      } catch {
        /* optional QR — panel omitted on failure */
      }
    };

    load();

    return () => {
      cancelled = true;
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = [];
    };
  }, [festivalId, status]);

  if (!festivalId || status === 'DELETED') {
    return null;
  }

  const showPublic = status === 'PUBLISHED' || status === 'PREVIEW';
  const showPrivate = status === 'UNPUBLISHED' || status === 'PREVIEW';

  if (!showPublic && !showPrivate) {
    return null;
  }

  const hasAny = (showPublic && publicUrl) || (showPrivate && privateUrl);

  return (
    <>
      {showPublic && (
        <QrCodePanel
          title="Public QR Code"
          description="Use this QR code on your advertising for the live festival link."
          imageUrl={publicUrl}
          linkHref={publicLinkHref}
          downloadName={`festival-${festivalId}-public-qr.png`}
        />
      )}
      {showPrivate && (
        <QrCodePanel
          title="Preview QR Code"
          description="Share this code for a private preview before publishing."
          imageUrl={privateUrl}
          linkHref={privateLinkHref}
          downloadName={`festival-${festivalId}-preview-qr.png`}
        />
      )}
      {!hasAny && (showPublic || showPrivate) && (
        <div className="glass-panel p-6">
          <p className="m-0 text-sm text-[var(--festy-muted-text)]">
            QR codes are loading…
          </p>
        </div>
      )}
    </>
  );
}

/** @deprecated Inline layout — use FestivalQrCodesSidebar on edit form. */
export const FestivalPromotionImages = FestivalQrCodesSidebar;
